import View from "./view.js";
import { esc } from "../escape.js";
import { itemImageSrc, DEFAULT_EMOJI } from "../itemVisual.js";
import { formatMoney } from "../currency.js";

class MenuListView extends View {
  _parentElement = document.querySelector(".modal-parent");
  _openBtn = document.querySelector("#menu-list");
  // Category manager starts collapsed so the modal opens focused on items.
  // Sticky across re-renders (e.g. deleting a category) so it doesn't snap shut mid-manage.
  _categoriesOpen = false;
  // Bulk select mode
  _selectMode = false;
  _selectedIds = new Set();

  _generateMarkUp() {
    const cap = (s) => s[0].toUpperCase() + s.slice(1);
    const { menuCategories: categories, menuItems } = this._data;

    const userCategories = categories.filter((c) => c !== 'uncategorized');

    const chips = userCategories.map((cat) => `
      <span class="menu-cat-chip">
        <span class="menu-cat-chip-name">${esc(cap(cat))}</span>
        <button class="menu-cat-delete" data-category="${esc(cat)}" type="button">×</button>
      </span>`).join('');

    const fmtMoney = (n) => formatMoney(n);

    // Variant option prices are additive (+$X), so a variant item's true floor
    // is base + the cheapest option in each group. Show that as "From $X"
    // instead of the misleading base price (often $0 for variant-priced items).
    const priceLabel = (item) => {
      const base = Number(item.price) || 0;
      if (item.hasVariants && Array.isArray(item.variants) && item.variants.length) {
        const minAddon = item.variants.reduce((sum, g) => {
          const prices = (g.options || []).map((o) => Number(o.optionPrice) || 0);
          return sum + (prices.length ? Math.min(...prices) : 0);
        }, 0);
        return `From ${fmtMoney(base + minAddon)}`;
      }
      return fmtMoney(base);
    };

    const renderCard = (item) => {
      // The square is always "the picture" (real photo, or the neutral
      // placeholder) - never an emoji tile, so it never duplicates the emoji
      // badge below. On a broken real image, clear alt too so the
      // placeholder icon shows instead of truncated alt text in a broken box.
      const src = itemImageSrc(item);
      const imgMarkup = `<img src="${esc(src)}" alt="${esc(item.itemName)}" loading="lazy" onerror="this.alt='';this.className='pos-item-img--ph';this.removeAttribute('src')">`;
      // A chosen (or default) emoji "pops" as a small corner badge on every
      // card's image tile instead of replacing it, so the square always
      // keeps its picture look and the grid reads consistently regardless
      // of whether a given item happens to have a real photo.
      const emojiBadge = `<span class="ml-item-emoji-badge" aria-hidden="true">${esc(item.iconEmoji || DEFAULT_EMOJI)}</span>`;
      const selected = this._selectedIds.has(item._id);
      const statusBadge = item.status && item.status !== 'active'
        ? `<span class="card-status card-status--${esc(item.status)}">${esc(cap(item.status))}</span>`
        : '';
      return `
      <div class="card${selected ? ' card--selected' : ''}" data-id="${item._id}">
        <div class="btn-main">
          <div class="ml-item-visual">${imgMarkup}${emojiBadge}</div>
          <div class="btn-main-text">
            <div class="title">${esc(item.itemName)}${statusBadge}</div>
            <div class="hint">${priceLabel(item)}</div>
          </div>
        </div>
        <span class="card-check" aria-hidden="true"></span>
      </div>`;
    };

    const groups = userCategories.map((cat) => {
      const items = menuItems.filter((i) => i.category === cat);
      if (!items.length) return `
        <div class="menu-cat-group">
          <p class="menu-cat-label">${esc(cap(cat))}</p>
          <p class="menu-cat-empty">No items in this category yet.</p>
        </div>`;
      return `
        <div class="menu-cat-group">
          <p class="menu-cat-label">${esc(cap(cat))}</p>
          <div class="menu-items-grid">${items.map(renderCard).join('')}</div>
        </div>`;
    }).join('');

    const uncategorizedItems = menuItems.filter(
      (i) => !i.category || i.category === 'uncategorized' || !categories.includes(i.category)
    );
    const uncategorizedGroup = uncategorizedItems.length ? `
      <div class="menu-cat-group">
        <p class="menu-cat-label menu-cat-label--muted">Uncategorized</p>
        <div class="menu-items-grid">${uncategorizedItems.map(renderCard).join('')}</div>
      </div>` : '';

    const hasAnyContent = userCategories.length > 0 || uncategorizedItems.length > 0;

    return `
      <div class="modal-overlay" id="menuModal">
        <div class="menu-modal${this._selectMode ? ' menu-modal--selecting' : ''}">
          <div class="menu-modal-header">
            <div class="menu-modal-heading">
              <h2 class="menu-modal-title">Catalogue</h2>
              <p class="menu-modal-subtitle">Add items one by one, or import your whole menu from a CSV.</p>
            </div>
            <div class="menu-header-actions">
              ${hasAnyContent ? `
                <button class="btn menu-select-toggle" id="menuSelectToggle" type="button">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="m9 12 2 2 4-4"/></svg>
                  Select
                </button>
              ` : ''}
              <button class="btn menu-csv-btn" id="menuUploadCsvBtn" type="button">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                Upload CSV
              </button>
              <button class="btn primary" id="openAddModal">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                Add Item
              </button>
            </div>
            <button class="modal-close" aria-label="Close">&times;</button>
          </div>
          <div class="menu-cat-manage${this._categoriesOpen ? ' menu-cat-manage--open' : ''}">
            <button type="button" class="menu-cat-toggle" id="menuCatToggle" aria-expanded="${this._categoriesOpen}">
              <svg class="menu-cat-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
              <span class="menu-cat-toggle-label">Categories</span>
              <span class="menu-cat-count">${userCategories.length}</span>
              <span class="menu-cat-toggle-hint">Manage</span>
            </button>
            <div class="menu-cat-body">
              <div class="menu-cat-chips">${chips || '<span class="menu-cat-hint">No categories yet.</span>'}</div>
              <div class="menu-cat-add-row">
                <input type="text" id="menuCategoryInput" placeholder="New category name…" class="category-input" />
                <button class="btn primary" id="menuAddCategoryBtn" type="button">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
                  Add Category
                </button>
              </div>
            </div>
          </div>
          <div class="menu-items-area">
            ${!hasAnyContent ? '<p class="menu-empty">No items yet. Add a category and your first item above.</p>' : ''}
            ${groups}
            ${uncategorizedGroup}
          </div>
          <div class="menu-select-bar" role="toolbar" aria-label="Bulk actions">
            <div class="menu-select-info">
              <span class="menu-select-count">${this._selectedIds.size}</span> selected
              <button class="menu-select-all" type="button">Select all</button>
            </div>
            <div class="menu-select-actions">
              <button class="menu-select-action" data-bulk-status="active" type="button">Set Active</button>
              <button class="menu-select-action" data-bulk-status="inactive" type="button">Set Inactive</button>
              <button class="menu-select-action" data-bulk-status="unavailable" type="button">Set Unavailable</button>
              <button class="menu-select-action menu-select-delete" data-bulk-delete type="button">Delete</button>
            </div>
            <button class="menu-select-cancel" type="button">Done</button>
          </div>
        </div>
      </div>`;
  }

  _addHandlerShowModal(handler) {
    this._openBtn.addEventListener("click", function (e) {
      e.preventDefault();
      handler();
    });
  }

  // Re-render in place after a data change (item add/edit/delete, bulk ops,
  // category delete). Suppresses the modal's entrance animation and slides the
  // content areas in instead, so updates read as a refresh - not a reopening
  // modal "blink". Falls back to a normal render when the catalogue isn't open.
  refresh(data) {
    const wasOpen = !!this._parentElement.querySelector(".menu-modal");
    this.render(data);
    if (!wasOpen) return;
    this._parentElement.querySelector(".menu-modal")?.classList.add("menu-modal--soft");
  }

  _addHandlerAddCategory(handler) {
    const submit = (input) => {
      if (!input?.value.trim()) return;
      handler(input.value.trim());
      input.value = '';
    };
    this._parentElement.addEventListener('click', (e) => {
      if (!e.target.closest('#menuAddCategoryBtn')) return;
      submit(this._parentElement.querySelector('#menuCategoryInput'));
    });
    this._parentElement.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || !e.target.matches('#menuCategoryInput')) return;
      submit(e.target);
    });
  }

  // Surgically update only the chips strip — no full modal re-render flash
  _updateChips(categories, newCategoryName = null) {
    const container = this._parentElement.querySelector('.menu-cat-chips');
    if (!container) return;

    const cap = (s) => s[0].toUpperCase() + s.slice(1);
    const userCategories = categories.filter((c) => c !== 'uncategorized');

    const countEl = this._parentElement.querySelector('.menu-cat-count');
    if (countEl) countEl.textContent = userCategories.length;

    container.innerHTML = userCategories.map((cat) => `
      <span class="menu-cat-chip">
        <span class="menu-cat-chip-name">${esc(cap(cat))}</span>
        <button class="menu-cat-delete" data-category="${esc(cat)}" type="button">×</button>
      </span>`).join('') || '<span class="menu-cat-hint">No categories yet.</span>';

    // Animate only the newly added chip
    if (newCategoryName) {
      const newChip = [...container.querySelectorAll('.menu-cat-chip')].find(
        el => el.querySelector('.menu-cat-chip-name')?.textContent.trim().toLowerCase()
              === newCategoryName.trim().toLowerCase()
      );
      if (newChip) newChip.classList.add('menu-cat-chip--entering');
    }
  }

  _addHandlerToggleCategories() {
    this._parentElement.addEventListener('click', (e) => {
      const btn = e.target.closest('#menuCatToggle');
      if (!btn || !btn.closest('#menuModal')) return;
      const manage = btn.closest('.menu-cat-manage');
      this._categoriesOpen = manage.classList.toggle('menu-cat-manage--open');
      btn.setAttribute('aria-expanded', this._categoriesOpen ? 'true' : 'false');
    });
  }

  _addHandlerUploadCsv(handler) {
    this._parentElement.addEventListener('click', (e) => {
      const btn = e.target.closest('#menuUploadCsvBtn');
      if (!btn || !btn.closest('#menuModal')) return;
      handler();
    });
  }

  // ── Bulk select ──────────────────────────────────────────────
  _enterSelectMode() {
    this._selectMode = true;
    this._parentElement.querySelector('.menu-modal')?.classList.add('menu-modal--selecting');
  }

  _exitSelectMode() {
    this._selectMode = false;
    this._selectedIds.clear();
    const modal = this._parentElement.querySelector('.menu-modal');
    modal?.classList.remove('menu-modal--selecting');
    modal?.querySelectorAll('.card--selected').forEach((c) => c.classList.remove('card--selected'));
    this._updateSelectBar();
  }

  _updateSelectBar() {
    const countEl = this._parentElement.querySelector('.menu-select-count');
    if (countEl) countEl.textContent = this._selectedIds.size;
  }

  _toggleCard(card) {
    const id = card.dataset.id;
    if (this._selectedIds.has(id)) {
      this._selectedIds.delete(id);
      card.classList.remove('card--selected');
    } else {
      this._selectedIds.add(id);
      card.classList.add('card--selected');
    }
    this._updateSelectBar();
  }

  _selectAll() {
    const cards = [...this._parentElement.querySelectorAll('#menuModal .menu-items-area .card')];
    const allSelected = cards.length > 0 && this._selectedIds.size === cards.length;
    this._selectedIds.clear();
    cards.forEach((c) => {
      if (allSelected) {
        c.classList.remove('card--selected');
      } else {
        this._selectedIds.add(c.dataset.id);
        c.classList.add('card--selected');
      }
    });
    this._updateSelectBar();
  }

  _addHandlerToggleSelectMode() {
    this._parentElement.addEventListener('click', (e) => {
      if (!e.target.closest('#menuSelectToggle')) return;
      if (this._selectMode) this._exitSelectMode();
      else this._enterSelectMode();
    });
  }

  _addHandlerCardSelect() {
    this._parentElement.addEventListener('click', (e) => {
      if (!this._selectMode) return;
      const card = e.target.closest('#menuModal .menu-items-area .card');
      if (!card) return;
      this._toggleCard(card);
    });
  }

  _addHandlerSelectBar({ onSetStatus, onDelete }) {
    this._parentElement.addEventListener('click', (e) => {
      if (!e.target.closest('#menuModal .menu-select-bar')) return;
      if (e.target.closest('.menu-select-cancel')) { this._exitSelectMode(); return; }
      if (e.target.closest('.menu-select-all')) { this._selectAll(); return; }
      const statusBtn = e.target.closest('[data-bulk-status]');
      if (statusBtn) { onSetStatus(statusBtn.dataset.bulkStatus, [...this._selectedIds]); return; }
      if (e.target.closest('[data-bulk-delete]')) { onDelete([...this._selectedIds]); return; }
    });
  }

  _addHandlerDeleteCategory(handler) {
    this._parentElement.addEventListener('click', (e) => {
      const btn = e.target.closest('.menu-cat-delete');
      if (!btn || !btn.closest('#menuModal')) return;
      handler(btn.dataset.category);
    });
  }

  _addHandlerCloseModal() {
    this._parentElement.addEventListener("click", (e) => {
      const btn = e.target.closest(".modal-close");
      if (!btn || !btn.closest("#menuModal")) return;
      const inner = this._parentElement.querySelector(".menu-modal");
      if (inner) inner.classList.add("modal-exiting");
      setTimeout(() => {
        if (inner) inner.classList.remove("modal-exiting");
        const overlay = this._parentElement.querySelector("#menuModal");
        if (overlay) overlay.classList.add("hidden");
      }, 220);
      e.preventDefault();
    });
  }
}

export default new MenuListView();
